import React from "react"
import styled, { keyframes, css } from "styled-components"
import { motion, AnimatePresence } from "framer-motion"
import {
  Check as CheckIcon,
  PlayArrow as PlayArrowIcon,
  Error as ErrorIcon,
} from "@mui/icons-material"

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: rgba(13, 12, 29, 0.6);
  border-radius: 12px;
  margin: 20px 0;
`

const StepContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ active }) => (active ? "rgba(79, 141, 251, 0.1)" : "transparent")};
  border-radius: 8px;
  border: 1px solid ${({ active }) => (active ? "var(--accent-blue)" : "transparent")};
  transition: all 0.3s ease;
`

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`

const IconContainer = styled(motion.div)`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ status }) =>
    status === "complete"
      ? "#4CAF50"
      : status === "active"
      ? "var(--accent-blue)"
      : status === "error"
      ? "#f44336"
      : "var(--secondary-text)"};
  
  ${({ status }) =>
    status === "active" &&
    css`
      animation: ${pulse} 1.5s ease-in-out infinite;
    `}
`

const StepText = styled.span`
  color: ${({ active }) => (active ? "var(--primary-text)" : "var(--secondary-text)")};
  font-size: 1rem;
  flex-grow: 1;
`

const StepDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-grow: 1;
`

const StepMessage = styled.span`
  color: var(--secondary-text);
  font-size: 0.85rem;
  margin-top: 2px;
`

const progressSteps = {
  init: "Initializing agents",
  text_to_sql: "Generating SQL query",
  text_to_sql_complete: "SQL query generated",
  text_to_python: "Generating Python code",
  text_to_python_complete: "Python code generated",
  validation_sql: "Validating SQL query",
  validation_sql_complete: "SQL query validated",
  validation_python: "Validating Python code",
  validation_python_complete: "Python code validated",
  execute_sql: "Executing SQL query",
  execute_sql_complete: "SQL query executed",
  execute_python: "Executing Python code",
  execute_python_complete: "Python code executed",
  compare_results: "Comparing results",
  compare_results_complete: "Results compared",
  visualization: "Generating visualization",
  visualization_complete: "Visualization generated",
  docx: "Generating report",
  docx_complete: "Report generated",
  format_results: "Formatting results",
  format_results_complete: "Results formatted",
  save_history: "Saving history",
  save_history_complete: "History saved"
}

const ProgressIndicator = ({ messages }) => {
  // Get the current active step
  const getCurrentStep = () => {
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) return null;
    
    // If the last message is a completion message, show the next step
    if (lastMessage.step.includes('_complete')) {
      const nextStepIndex = Object.keys(progressSteps).findIndex(step => step === lastMessage.step) + 1;
      return Object.keys(progressSteps)[nextStepIndex] || null;
    }
    
    return lastMessage.step;
  };

  const currentStep = getCurrentStep();

  // Get the status of a step
  const getStepStatus = (step) => {
    const stepMessages = messages.filter(m => m.step === step || m.step === `${step}_complete`);
    const lastStepMessage = stepMessages[stepMessages.length - 1];

    if (!lastStepMessage) return "pending";
    if (lastStepMessage.step.includes("error")) return "error";
    if (lastStepMessage.step.includes("_complete")) return "complete";
    return "active";
  };

  const stepVariants = {
    hidden: { opacity: 0, height: 0, marginBottom: 0 },
    visible: { opacity: 1, height: "auto", marginBottom: 16 },
    exit: { opacity: 0, height: 0, marginBottom: 0 }
  };

  // Filter steps to show only the current active step and the last completed step
  const visibleSteps = Object.entries(progressSteps).filter(([step]) => {
    if (!currentStep) return false;
    
    const currentStepIndex = Object.keys(progressSteps).indexOf(currentStep);
    const stepIndex = Object.keys(progressSteps).indexOf(step);
    
    // Show the current step
    if (step === currentStep) return true;
    
    // Show the last completed step
    if (stepIndex === currentStepIndex - 1 && getStepStatus(step) === "complete") return true;
    
    return false;
  });

  return (
    <ProgressContainer>
      <AnimatePresence mode="wait">
        {visibleSteps.map(([step, label]) => {
          const status = getStepStatus(step);
          const message = messages.find(m => m.step === step)?.message;

          return (
            <StepContainer
              key={step}
              active={status === "active"}
              variants={stepVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              <IconContainer status={status}>
                {status === "complete" ? (
                  <CheckIcon fontSize="small" />
                ) : status === "active" ? (
                  <PlayArrowIcon fontSize="small" />
                ) : status === "error" ? (
                  <ErrorIcon fontSize="small" />
                ) : (
                  <div style={{ width: 18, height: 18 }} />
                )}
              </IconContainer>
              <StepDetails>
                <StepText active={status === "active"}>{label}</StepText>
                {message && <StepMessage>{message}</StepMessage>}
              </StepDetails>
            </StepContainer>
          )
        })}
      </AnimatePresence>
    </ProgressContainer>
  )
}

export default ProgressIndicator 